// ============================================================
// CRITICAL DAMAGE TABLES (compact) - Pure data + lookup
// ============================================================
// CT[type][loc][severity-1] = "text|effects"
// Effects: S=Stunned,B=Blinded,D=Deafened,P=Prone,F=Fatigue,BL=BloodLoss,BLS=BloodLoss(severe),
// Fi=OnFire,U=Unconscious,Cr=Crippled,De=Destroyed,Dy=Dying,Pa=Paralysed,NS=NoSpeak,K=Dead,Dr=Drop

import type { CritResult } from '../types';
import { getVehicleCrit } from './vehicleCriticalTables';
import { getAmorphousCrit } from './amorphousCriticalTables';

const CT: Record<string, Record<string, string[]>> = {};
function cc(t: string, l: string, entries: string[]): void {
  if (!CT[t]) CT[t] = {};
  CT[t][l] = entries;
}

cc('I', 'head', [
  'Jarring blow. Stunned 1 round.|S1',
  'Stunned 1 round. Helmeted: unaffected by further head hits.|S1',
  'Heavy impact. Stunned 1d5 rounds, Toughness test or 1 Fatigue.|S1d5,F1',
  'Skull cracked. Stunned 1d5 rounds, 1d5 Fatigue, Blinded 1d5 rounds.|S1d5,F1d5,B1d5',
  'Devastating concussion. Stunned 1d10 rounds, Prone, Blinded 1d10. T(-20) or Unconscious.|S1d10,P,B1d10',
  'Jaw shattered. Stunned 1d10 rounds, cannot speak, 1d5 Fatigue. -10 Fel.|S1d10,NS,F1d5,Pen:fel=-10',
  'Skull fractured. Unconscious 1d10 hours. -1d10 Int. Blood Loss.|U,BL,Pen:int=-1d10',
  'Skull caved in. Unconscious, dying 1d5 rounds. -1d10 Int, -1d10 Per. Blood Loss.|U,Dy1d5,BL,Pen:int=-1d10,Pen:per=-1d10',
  'Head pulped. Killed instantly.|K',
  'Head explodes. Killed instantly.|K',
]);
cc('I', 'arm', [
  'Arm jarred. Drops held items.|Dr',
  'Numbing blow. Drops held, arm unusable 1 round.|Dr,Pen:Arm unusable:1',
  'Arm struck hard. 1 Fatigue. -10 arm tests 1d5 rounds.|F1,Pen:-10 arm tests:1d5',
  'Muscles torn. Drops held, arm useless 1d10 rounds. 2 Fatigue.|Dr,F2,Pen:Arm useless:1d10',
  'Bones cracked. Arm Crippled. -30 arm tests. Blood Loss.|Cr,BL',
  'Arm broken. Crippled. Stunned 1 round. 1d5 Fatigue. Blood Loss.|Cr,S1,F1d5,BL',
  'Arm shattered. Crippled, useless until surgery. Stunned 1d5 rounds. Blood Loss.|Cr,S1d5,BL',
  'Arm crushed. Destroyed. Stunned 1d10 rounds. Blood Loss. T(-20) or die.|De,S1d10,BL',
  'Arm ripped off. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Arm destroyed utterly. Dies unless Fate Point.|K',
]);
cc('I', 'body', [
  'Bruising blow. Stunned 1 round.|S1',
  'Winded. Stunned 1 round, knocked back 1d5m.|S1',
  'Ribs cracked. 1d5 Fatigue. -10 Agility 1d10 rounds.|F1d5,Pen:agi=-10:1d10',
  'Knocked flat. Stunned 1 round, Prone, knocked back 1d5m. 1 Fatigue.|S1,P,F1',
  'Ribs broken. Stunned 1d5 rounds, Prone. 1d10 Fatigue. Blood Loss.|S1d5,P,F1d10,BL',
  'Organs damaged. Stunned 1d10 rounds, Prone. Blood Loss. T or Unconscious.|S1d10,P,BL',
  'Internal haemorrhaging. Unconscious. Blood Loss (severe). Dies w/o treatment 1d5 rounds.|U,BLS,Dy1d5',
  'Spine cracked. Paralysed waist down. Unconscious. Blood Loss.|Pa,U,BL',
  'Chest caved in. Dies 1d5 rounds unless Fate Point.|Dy1d5',
  'Torso destroyed. Killed instantly.|K',
]);
cc('I', 'leg', [
  'Stumble. Agility test or Prone.|',
  'Knee buckles. Prone.|P',
  'Leg numbed. -10 Movement/Dodge 1d5 rounds. 1 Fatigue.|F1,Pen:movement+dodge=-10:1d5',
  'Leg struck hard. Prone. -20 Dodge/Move 1d10 rounds. 1d5 Fatigue.|P,F1d5,Pen:dodge+movement=-20:1d10',
  'Knee shattered. Leg Crippled. Prone. Movement halved. Blood Loss.|Cr,P,BL',
  'Leg broken. Crippled. Prone. Stunned 1 round. 1d5 Fatigue. Blood Loss.|Cr,P,S1,F1d5,BL',
  'Leg shattered. Crippled, useless. Prone. Stunned 1d5 rounds. Blood Loss.|Cr,P,S1d5,BL',
  'Leg crushed. Destroyed. Prone. Stunned 1d10 rounds. Blood Loss (severe). T(-20) or die.|De,P,S1d10,BLS',
  'Leg ripped away. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Leg and hip destroyed. Killed instantly.|K',
]);
cc('E', 'head', [
  'Scorched. Stunned 1 round.|S1',
  'Flash burns. Blinded 1 round. Stunned 1 round.|B1,S1',
  'Serious burns. Stunned 1d5 rounds, Blinded 1d5. -10 Fel.|S1d5,B1d5,Pen:fel=-10',
  'Eyes seared. Blinded 1d10, Stunned 1d5. 1d5 Fatigue. T or permanent Blind.|B1d10,S1d5,F1d5',
  'Face melted. Stunned 1d10, permanently Blinded. -20 Fel. Blood Loss.|S1d10,B,BL,Pen:fel=-20',
  'Head engulfed. On Fire. Stunned 1d10. Permanently Blinded/Deafened.|Fi,S1d10,B,D',
  'Brain cooked. Unconscious. Dies 1d5 rounds w/o treatment.|U,Dy1d5',
  'Head burned to skull. Dying 1d5 rounds.|Dy1d5',
  'Head burned away. Killed instantly.|K',
  'Head vapourised. Killed instantly.|K',
]);
cc('E', 'arm', [
  'Hand scorched. Drops held.|Dr',
  'Arm burned. -10 arm tests 1d5 rounds. Drops held.|Dr,Pen:-10 arm tests:1d5',
  'Severe burns. Arm useless 1d10 rounds. 1 Fatigue.|F1,Pen:Arm useless:1d10',
  'Muscles seared. Arm Crippled. Blood Loss.|Cr,BL',
  'Arm on fire. Arm Crippled. 1d5 Fatigue. Blood Loss.|Fi,Cr,F1d5,BL',
  'Arm burned through. Crippled, useless. Stunned 1 round. Blood Loss.|Cr,S1,BL',
  'Arm burned to bone. Destroyed. Stunned 1d5 rounds. Blood Loss (severe).|De,S1d5,BLS',
  'Arm incinerated. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Arm/shoulder burned away. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Arm vapourised. Killed instantly.|K',
]);
cc('E', 'body', [
  'Scorched. Stunned 1 round.|S1',
  'Surface burns. Stunned 1 round. 1 Fatigue.|S1,F1',
  'Serious burns. On Fire. 1d5 Fatigue.|Fi,F1d5',
  'Chest seared. On Fire. Stunned 1d5 rounds. Blood Loss.|Fi,S1d5,BL',
  'Organs cooking. On Fire. Stunned 1d10. Prone. Blood Loss.|Fi,S1d10,P,BL',
  'Torso engulfed. On Fire. Unconscious. Blood Loss (severe). Dies w/o treatment.|Fi,U,BLS,Dy',
  'Organs boiling. Unconscious. Dies 1d5 rounds.|U,Dy1d5',
  'Burning from within. Dying 1d3 rounds.|Dy1d3',
  'Torso burned through. Killed instantly.|K',
  'Body immolated. Killed instantly.|K',
]);
cc('E', 'leg', [
  'Foot scorched. Agility or Prone.|',
  'Leg burned. Prone. -10 Move 1d5 rounds.|P,Pen:movement=-10:1d5',
  'Severe burns. Leg useless 1d5 rounds. 1 Fatigue. Prone.|F1,P,Pen:Leg useless:1d5',
  'Knee seared. Leg Crippled. Prone. Blood Loss.|Cr,P,BL',
  'Leg on fire. Leg Crippled. 1d5 Fatigue. Blood Loss.|Fi,Cr,F1d5,BL',
  'Leg burned through. Crippled, useless. Stunned 1 round. Blood Loss.|Cr,S1,BL',
  'Leg burned to bone. Destroyed. Prone. Stunned 1d5. Blood Loss (severe).|De,P,S1d5,BLS',
  'Leg incinerated. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Leg/hip burned away. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Lower body engulfed. Killed instantly.|K',
]);
cc('R', 'head', [
  'Grazing cut. Stunned 1 round.|S1',
  'Deep laceration. Stunned 1 round. Blood Loss.|S1,BL',
  'Ear sliced. Deafened. Stunned 1d5 rounds. Blood Loss.|D,S1d5,BL',
  'Face slashed. Stunned 1d5, Blinded 1d5. -10 Fel. Blood Loss.|S1d5,B1d5,BL,Pen:fel=-10',
  'Eye destroyed. -10 BS permanent. Stunned 1d10. Blood Loss.|S1d10,BL,Pen:bs=-10',
  'Throat slashed. Cannot speak. Blood Loss (severe). Stunned 1d10. Dies 1d5 w/o treatment.|NS,BLS,S1d10,Dy1d5',
  'Face flayed. Permanently Blinded/Deafened. Unconscious. Blood Loss (severe).|B,D,U,BLS',
  'Skull opened. Unconscious, dying 1d5 rounds.|U,Dy1d5',
  'Head split open. Killed instantly.|K',
  'Decapitated. Killed instantly.|K',
]);
cc('R', 'arm', [
  'Hand cut. Drops held.|Dr',
  'Deep gash. -10 arm tests. Blood Loss.|BL,Pen:-10 arm tests',
  'Tendons slashed. Arm useless 1d10 rounds. Blood Loss.|BL,Pen:Arm useless:1d10',
  'Muscles severed. Arm Crippled. Blood Loss.|Cr,BL',
  'Deep wound. Arm Crippled. Stunned 1 round. Blood Loss.|Cr,S1,BL',
  'Arm laid open. Crippled, useless. Blood Loss (severe).|Cr,BLS',
  'Hand severed. Blood Loss (severe). Stunned 1d5 rounds.|De,BLS,S1d5',
  'Arm severed at elbow. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Arm severed at shoulder. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Arm hacked apart. Killed instantly.|K',
]);
cc('R', 'body', [
  'Flesh wound. Stunned 1 round. Blood Loss.|S1,BL',
  'Deep cut. 1 Fatigue. Blood Loss.|F1,BL',
  'Gash across chest. 1d5 Fatigue. Blood Loss.|F1d5,BL',
  'Abdomen slashed. Stunned 1d5 rounds. Blood Loss.|S1d5,BL',
  'Ribcage cut open. Stunned 1d10. Prone. Blood Loss (severe).|S1d10,P,BLS',
  'Organs lacerated. Unconscious. Blood Loss (severe). Dies w/o treatment.|U,BLS,Dy',
  'Chest opened. Unconscious, dying 1d5 rounds.|U,Dy1d5',
  'Bisecting wound. Dying 1d3 rounds.|Dy1d3',
  'Eviscerated. Killed instantly.|K',
  'Cut in half. Killed instantly.|K',
]);
cc('R', 'leg', [
  'Grazed. Agility or Prone. Blood Loss.|BL',
  'Calf slashed. Prone. Blood Loss.|P,BL',
  'Deep cut to thigh. 1d5 Fatigue. Blood Loss.|F1d5,BL',
  'Hamstring severed. Leg Crippled. Prone. Blood Loss.|Cr,P,BL',
  'Knee slashed. Leg Crippled. Stunned 1 round. Blood Loss (severe).|Cr,S1,BLS',
  'Leg cut to bone. Crippled, useless. Blood Loss (severe).|Cr,BLS',
  'Foot severed. Blood Loss (severe). Stunned 1d5 rounds.|De,BLS,S1d5',
  'Leg severed at knee. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Leg severed at hip. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Leg hacked away. Killed instantly.|K',
]);
cc('X', 'head', [
  'Dazed. Stunned 1 round. Deafened 1d5.|S1,D1d5',
  'Flash/bang. Stunned 1d5. Blinded 1 round. Deafened 1d5.|S1d5,B1,D1d5',
  'Shrapnel to face. Stunned 1d5. Blinded 1d5. -10 Fel. Blood Loss.|S1d5,B1d5,BL,Pen:fel=-10',
  'Jaw blown off. Stunned 1d10. Cannot speak. Blood Loss. -20 Fel.|S1d10,NS,BL,Pen:fel=-20',
  'Eye destroyed. -10 BS perm. Stunned 1d10. Deafened perm. Blood Loss.|S1d10,D,BL,Pen:bs=-10',
  'Face destroyed. Permanently Blinded/Deafened. Unconscious. Blood Loss (severe).|B,D,U,BLS',
  'Skull blown open. Unconscious, dying 1d5 rounds.|U,Dy1d5',
  'Head shattered. Dying 1d3 rounds.|Dy1d3',
  'Head blown apart. Killed instantly.|K',
  'Head detonates. Killed instantly. All within 2m take shrapnel.|K',
]);
cc('X', 'arm', [
  'Hand shredded. Drops held.|Dr',
  'Forearm peppered. -10 arm tests. Blood Loss.|BL,Pen:-10 arm tests',
  'Elbow blown out. Arm useless 1d10 rounds. Blood Loss.|F1d5,BL,Pen:Arm useless:1d10',
  'Arm torn open. Arm Crippled. Blood Loss.|Cr,BL',
  'Bones shattered. Arm Crippled. Stunned 1 round. Blood Loss (severe).|Cr,S1,BLS',
  'Arm mangled. Crippled, useless. Blood Loss (severe).|Cr,BLS',
  'Hand blown off. Blood Loss (severe). Stunned 1d5 rounds.|De,BLS,S1d5',
  'Arm blown off at elbow. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Arm/shoulder destroyed. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Arm detonates. Killed instantly.|K',
]);
cc('X', 'body', [
  'Concussive blast. Stunned 1 round. Knocked back 1d5m.|S1',
  'Shrapnel wounds. Stunned 1 round. Blood Loss.|S1,BL',
  'Ribs cracked. Stunned 1d5. Prone. Blood Loss.|S1d5,P,BL',
  'Chest torn. Stunned 1d10. Prone. Blood Loss.|S1d10,P,BL',
  'Organs ruptured. Unconscious. Blood Loss (severe). Dies w/o treatment.|U,BLS,Dy',
  'Torso ripped open. Unconscious, dying. Blood Loss (severe).|U,BLS,Dy',
  'Body cavity breached. Dying 1d5 rounds.|Dy1d5',
  'Torso shattered. Dying 1d3 rounds.|Dy1d3',
  'Body blown apart. Killed instantly.|K',
  'Massive detonation. Killed instantly. All within 3m take shrapnel.|K',
]);
cc('X', 'leg', [
  'Knocked off feet. Prone.|P',
  'Shin peppered. Prone. Blood Loss.|P,BL',
  'Knee blown out. Leg Crippled. Prone. Blood Loss.|Cr,P,BL',
  'Thigh torn. Leg Crippled. Stunned 1 round. Blood Loss.|Cr,S1,BL',
  'Leg shattered. Crippled, useless. Stunned 1d5. Blood Loss (severe).|Cr,S1d5,BLS',
  'Foot blown off. Blood Loss (severe). Stunned 1d5.|De,BLS,S1d5',
  'Leg blown off at knee. T(-20) or die. Blood Loss (severe).|De,BLS',
  'Leg/hip shattered. T(-30) or die. Blood Loss (severe).|De,BLS',
  'Leg destroyed. Dies unless Fate Point.|K',
  'Leg detonates. Killed instantly.|K',
]);

export { CT };

export function getCrit(dmgType: string, locId: string, severity: number): CritResult {
  let loc = locId;
  if (loc === 'rightArm' || loc === 'leftArm') loc = 'arm';
  if (loc === 'rightLeg' || loc === 'leftLeg') loc = 'leg';
  const sev = Math.min(Math.max(severity, 1), 10);
  if (CT[dmgType] && CT[dmgType][loc] && CT[dmgType][loc][sev - 1]) {
    const parts = CT[dmgType][loc][sev - 1].split('|');
    return { text: parts[0], fx: parts[1] || '', isDead: (parts[1] || '').indexOf('K') >= 0 };
  }
  return { text: 'Severe critical damage. GM determines effects.', fx: '', isDead: false };
}

/** An object past 0 Wounds: destroyed at once (the rulings, 2026-10-24) - `critOutcome` returns it before any table. */
export const OBJECT_DESTROYED: CritResult = { text: 'Destroyed.', fx: 'K', isDead: true };

/**
 * Polymorphic crit dispatch - routes to the appropriate crit table
 * based on body type (humanoid, vehicle, amorphous). An object never reaches it: it has no table.
 */
export function getCritForBody(
  dmgType: string,
  locId: string,
  severity: number,
  bodyType?: import('../types').BodyTypeId,
): CritResult {
  if (bodyType === 'vehicle') return getVehicleCrit(dmgType, locId, severity);
  if (bodyType === 'amorphous') return getAmorphousCrit(dmgType, severity);
  return getCrit(dmgType, locId, severity);
}
